import React from "react";
import { Card, Typography, Popover, Row, Col, Tooltip } from "antd";
import {
  Newspaper,
  ChartColumn,
  Clock,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import CountUp from "react-countup";
import styles from "./ConceptCard.module.scss";

const { Title, Paragraph, Text } = Typography;

const ConceptCard = ({
  data,
  doReload = false,
  LinkWrapper,
  backgroundColor,
}) => {
  const {
    value,
    definition,
    total_citations,
    total_publications,
    yearly_stats,
    publicationTrend,
  } = data;

  const getLatestCompleteYear = () => {
    return yearly_stats && yearly_stats.length > 1
      ? yearly_stats[yearly_stats.length - 2].year
      : null;
  };

  const latestCompleteYear = getLatestCompleteYear();

  const statsContent = (
    <div className={styles.statsPopover}>
      <Row gutter={[16, 16]}>
        {total_publications !== undefined && (
          <Col span={12}>
            <div className={styles.statItem}>
              <Newspaper className={styles.statIcon} />
              <div className={styles.statContent}>
                <div className={styles.statTitle}>Publications</div>
                <CountUp
                  start={0}
                  end={total_publications}
                  duration={0.75}
                  separator=","
                  className={styles.statValue}
                />
              </div>
            </div>
          </Col>
        )}
        {total_citations !== undefined && (
          <Col span={12}>
            <div className={styles.statItem}>
              <ChartColumn className={styles.statIcon} />
              <div className={styles.statContent}>
                <div className={styles.statTitle}>Citations</div>
                <CountUp
                  start={0}
                  end={total_citations}
                  duration={0.75}
                  separator=","
                  className={styles.statValue}
                />
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );

  const getTrendColor = (trend) => {
    if (!trend && trend !== 0) return null;
    if (trend > 5) return "#3f8600";
    if (trend > 0) return "#52c41a";
    if (trend < -5) return "#cf1322";
    if (trend < 0) return "#ff4d4f";
    return "#faad14";
  };

  const getTrendIcon = (trend) => {
    if (!trend && trend !== 0) return null;
    if (trend > 0) return <TrendingUp size={16} />;
    if (trend < 0) return <TrendingDown size={16} />;
    return null;
  };

  const CardContent = (
    <Popover
      content={
        total_publications !== undefined || total_citations !== undefined
          ? statsContent
          : null
      }
      title="Concept Statistics"
      trigger="hover"
      placement="right"
      overlayClassName={styles.statsPopoverOverlay}
    >
      <Card
        className={styles.conceptCard}
        hoverable
        style={{ backgroundColor }}
        onClick={() => {
          if (doReload) {
            window.location.reload();
          }
        }}
      >
        <div className={styles.cardContent}>
          <div className={styles.header}>
            <Title level={4} className={styles.title}>
              <span className={styles.titleGradient}>
                {value.toUpperCase()}
              </span>
            </Title>
          </div>
          <Paragraph
            className={styles.description}
            ellipsis={{
              rows: 3,
              expandable: true,
              symbol: "See more",
              onExpand: (e) => e.stopPropagation(),
            }}
          >
            {definition || "No definition available"}
          </Paragraph>
          <div className={styles.footer}>
            {latestCompleteYear && (
              <Tooltip title="Latest data year">
                <Text className={styles.latestYear}>
                  <Clock size={14} /> {latestCompleteYear}
                </Text>
              </Tooltip>
            )}
            {publicationTrend !== null && !isNaN(publicationTrend) && (
              <Tooltip title="Publication trend (last two years)">
                <Text
                  className={styles.trend}
                  style={{ color: getTrendColor(publicationTrend) }}
                >
                  {getTrendIcon(publicationTrend)}{" "}
                  {Math.abs(publicationTrend).toFixed(1)}%
                </Text>
              </Tooltip>
            )}
          </div>
        </div>
      </Card>
    </Popover>
  );

  return LinkWrapper ? (
    <LinkWrapper
      link={`/explore/concept/${data.id}`}
      component={CardContent}
      className={styles["link-wrapper"]}
    />
  ) : (
    CardContent
  );
};

export default ConceptCard;
